import { CommonTokenStream, InputStream } from 'antlr4';
import { ParseTreeWalker } from 'antlr4/tree/Tree';
import { SearchRegexLexer } from 'generated/compound-search/SearchRegexLexer';
import { SearchRegexParser } from 'generated/compound-search/SearchRegexParser';

import { SearchQueryTerm, SearchQueryTermType, SearchQueryTermWithoutPositions } from '../types';

import {
  mapSearchRegexParserErrorToErrorCode,
  SearchRegexQueryParserListener,
} from './node-tree-listener';
import { SearchRegexQueryParserSilentErrorListener } from './silent-error-listener';
import { SearchRegexQueryParserErrorCode, SearchRegexQueryParserResult } from './types';

export const createSearchRegexLanguageParser = () => {
  function parse(input: string) {
    const { parser, errorListener } = createParser(input);
    const listener = parseQuery(parser);
    return validateAndCreateResult(listener, errorListener);
  }

  function createParser(input: string) {
    const chars = new InputStream(input);
    const lexer = new SearchRegexLexer(chars);
    const tokens = new CommonTokenStream(lexer);
    const parser = new SearchRegexParser(tokens);

    const errorListener = configureErrorHandling(lexer, parser);

    return { parser, errorListener };
  }

  function configureErrorHandling(lexer: SearchRegexLexer, parser: SearchRegexParser) {
    const errorListener = new SearchRegexQueryParserSilentErrorListener();

    lexer.removeErrorListeners();
    lexer.addErrorListener(errorListener);
    parser.removeErrorListeners();
    parser.addErrorListener(errorListener);

    return errorListener;
  }

  function parseQuery(parser: SearchRegexParser) {
    const tree = parser.query();
    const listener = new SearchRegexQueryParserListener(parser.symbolicNames);
    ParseTreeWalker.DEFAULT.walk(listener, tree);

    return listener;
  }

  function validateAndCreateResult(
    listener: SearchRegexQueryParserListener,
    errorListener: SearchRegexQueryParserSilentErrorListener,
  ): SearchRegexQueryParserResult {
    const { status, query, errorCode, lastValidQuery } = listener;

    if (status === 'invalid') {
      return createInvalidResult(errorCode, lastValidQuery);
    }

    if (errorListener.error) {
      return createInvalidResult(
        mapSearchRegexParserErrorToErrorCode(errorListener.error),
        lastValidQuery,
      );
    }

    return createValidResult(query);
  }

  function createInvalidResult(
    errorCode: SearchRegexQueryParserErrorCode | undefined,
    query: SearchQueryTerm[],
  ): SearchRegexQueryParserResult {
    return {
      status: 'invalid',
      query,
      errorCode,
    };
  }

  function createValidResult(query: SearchQueryTerm[]): SearchRegexQueryParserResult {
    return {
      status: 'valid',
      query,
    };
  }

  function serialize(query: SearchQueryTermWithoutPositions[]): string {
    const parts: string[] = [];

    for (const node of query) {
      if (node.type === SearchQueryTermType.GROUP_START) {
        parts.push('(');
        continue;
      }

      if (node.type === SearchQueryTermType.GROUP_END) {
        parts.push(')');
        continue;
      }

      if (node.type === SearchQueryTermType.OPERATOR) {
        if (node.value === 'NOT') {
          parts.push('!');
          continue;
        }

        if (node.value === 'AND') {
          parts.push('&');
          continue;
        }

        if (node.value === 'OR') {
          parts.push('|');
          continue;
        }

        continue;
      }

      const escapedValue = node.value
        .replace(/\\x20/g, '\\\\x20')
        .replace(/ /g, '\\x20')
        .replace(/^!/g, '\\!');

      parts.push(escapedValue);
    }

    return joinTokens(parts);
  }

  return { parse, serialize };
};

export const searchRegexLanguageParser = createSearchRegexLanguageParser();

function joinTokens(tokens: string[]): string {
  const result: string[] = [];

  tokens.forEach((token, index) => {
    if (index === 0) {
      result.push(token);
      return;
    }

    const previousToken = tokens[index - 1];

    if (previousToken === '!') {
      result.push(token);
      return;
    }

    if (previousToken === '(') {
      result.push(' ', token);
      return;
    }

    if (token === '&' || token === '|') {
      result.push(' ', token);
      return;
    }

    result.push(' ', token);
  });

  return result.join('');
}
